// 결제내역(통합) 엑셀 변환 순수 함수 모음 (SPEC-RESELLER-PAYMENT-ADMIN-001).
// 별칭(@/)·axios import 없이 node:test 로 검증한다.
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { formatDateTime } from "./formatters.js";
import { PaymentStatusLabel } from "../enums.js";

const SHEET_NAME = "결제내역";
const PLACEHOLDER = "-";
const AMOUNT_FORMAT = "#,##0";

// @MX:NOTE: [AUTO] 수식 주입 이중 방어. 사용자 입력 7열(sanitize: true)에만 적용하고,
// 값이 없어서 넣는 자리표시 "-"는 처리 후 삽입하므로 "'-"로 바뀌지 않는다.
export const sanitizeForExcel = (value) => {
  if (!value) return "";
  const str = String(value);
  return /^[=+\-@\t\r\n]/.test(str) ? `'${str}` : str;
};

// @MX:NOTE: [AUTO] admin-reseller formatters.ts 의 extractBillingPeriod 와 같은 규칙.
// notes 형식이 바뀌면 두 곳을 함께 갱신한다. 못 찾으면 빈 문자열(행 생성에서 "-").
export const extractBillingPeriodForExcel = (notes) => {
  if (!notes) return "";
  const match = notes.match(/\((\d{4}년\s*\d{1,2}월[^)]*)\)/);
  if (!match) return "";
  return match[1].replace(/\s*\(?일할계산\)?/g, "").trim();
};

const isManual = (r) => (r.paymentMethod ?? "CARD") === "MANUAL";

const dateTimeOrEmpty = (v) => (v ? formatDateTime(v) : "");

// 열 정의: sanitize=true 는 사람이 입력하거나 외부에서 온 문자열 열, numeric=true 는 숫자 셀
export const PAYMENT_HISTORY_EXCEL_COLUMNS = [
  { header: "리셀러", width: 18, sanitize: true, getValue: (r) => r.resellerName },
  { header: "거래처", width: 20, sanitize: true, getValue: (r) => r.companyName },
  { header: "상품명", width: 15, sanitize: true, getValue: (r) => r.productName },
  {
    header: "구분",
    width: 10,
    getValue: (r) =>
      r.paymentType === "INSTALLATION_FEE" || r.paymentRound === 0
        ? "설치비"
        : `${r.paymentRound}회차`,
  },
  { header: "청구기간", width: 22, getValue: (r) => extractBillingPeriodForExcel(r.notes) },
  { header: "결제금액", width: 15, numeric: true, amount: true, getValue: (r) => r.amount || 0 },
  {
    header: "결제수단",
    width: 22,
    sanitize: true,
    getValue: (r) => {
      if (isManual(r)) return r.manualPaymentMethod || "수기결제";
      return [r.cardCompany, r.cardNumber].filter(Boolean).join(" ");
    },
  },
  { header: "결제일시", width: 18, getValue: (r) => dateTimeOrEmpty(r.paidAt) },
  { header: "등록일시", width: 18, getValue: (r) => dateTimeOrEmpty(r.createdAt) },
  {
    header: "상태",
    width: 10,
    getValue: (r) => PaymentStatusLabel[r.status] || r.status || "",
  },
  {
    header: "원결제금액",
    width: 15,
    numeric: true,
    amount: true,
    getValue: (r) => r.originalAmount ?? r.amount ?? 0,
  },
  {
    header: "취소금액",
    width: 12,
    numeric: true,
    amount: true,
    getValue: (r) => r.canceledAmount ?? "",
  },
  { header: "취소사유", width: 20, sanitize: true, getValue: (r) => r.cancelReason },
  { header: "취소일시", width: 18, getValue: (r) => dateTimeOrEmpty(r.canceledAt) },
  { header: "실패사유", width: 25, sanitize: true, getValue: (r) => r.failReason },
  {
    header: "처리방식",
    width: 10,
    getValue: (r) => {
      if (r.processedBy === "auto-payment-cron") return "자동결제";
      return isManual(r) ? "수기" : "수동";
    },
  },
  { header: "거래번호", width: 25, getValue: (r) => r.transactionId },
  { header: "주문번호", width: 25, getValue: (r) => r.orderId },
  { header: "메모", width: 30, sanitize: true, getValue: (r) => r.memo },
  { header: "영수증 URL", width: 35, getValue: (r) => r.receiptUrl },
];

const toCell = (column, record) => {
  const raw = column.getValue(record);
  if (column.numeric) return raw === "" ? "" : Number(raw);
  const text = raw === undefined || raw === null ? "" : String(raw);
  if (text === "") return PLACEHOLDER;
  return column.sanitize ? sanitizeForExcel(text) : text;
};

/** [머리글, ...행] 2차원 배열 */
export const buildPaymentHistorySheetRows = (records) => [
  PAYMENT_HISTORY_EXCEL_COLUMNS.map((c) => c.header),
  ...records.map((r) => PAYMENT_HISTORY_EXCEL_COLUMNS.map((c) => toCell(c, r))),
];

const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;

/** 파일명: 결제내역_{리셀러부}_{기간부}_{저장시각}.xlsx (REQ-RPA-020) */
export const buildPaymentHistoryFilename = ({
  resellerName,
  dateType,
  startDate,
  endDate,
  now = new Date(),
}) => {
  const resellerPart = resellerName
    ? resellerName.replace(INVALID_FILENAME_CHARS, "_")
    : "전체";
  const basis = dateType === "paidAt" ? "결제일기준" : "등록일기준";

  let periodPart = "전체기간";
  if (startDate && endDate) {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const isFullMonth =
      start.date() === 1 && end.isSame(start.endOf("month"), "day");
    periodPart = isFullMonth
      ? `${start.format("YYYY년MM월")}_${basis}`
      : `${start.format("YYYYMMDD")}-${end.format("YYYYMMDD")}_${basis}`;
  }

  return `결제내역_${resellerPart}_${periodPart}_${dayjs(now).format("YYYYMMDD_HHmmss")}.xlsx`;
};

/** 워크북 생성 (파일 쓰기 전 단계 — 테스트 대상) */
export const buildPaymentHistoryWorkbook = (rows) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = PAYMENT_HISTORY_EXCEL_COLUMNS.map((c) => ({ wch: c.width }));
  ws["!autofilter"] = { ref: ws["!ref"] };

  // 금액 열의 숫자 셀에 천단위 서식
  PAYMENT_HISTORY_EXCEL_COLUMNS.forEach((c, colIdx) => {
    if (!c.amount) return;
    for (let rowIdx = 1; rowIdx < rows.length; rowIdx += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: rowIdx, c: colIdx })];
      if (cell && cell.t === "n") cell.z = AMOUNT_FORMAT;
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);
  return wb;
};

/** 브라우저에서 xlsx 파일로 저장 */
export const writePaymentHistoryWorkbook = (rows, filename) => {
  XLSX.writeFile(buildPaymentHistoryWorkbook(rows), filename);
};
